import React, { useMemo } from 'react';
import './ScheduleView.css';
import { config } from '../../config';
import { OlympicsListableEvent } from '../../types/olympicsListableEvent';
import { ResultsIndividualEvent } from './ResultsIndividualEvent';
import { ResultsTeamEvent } from './ResultsTeamEvent';
import { TeamSportLayout } from './TeamSportLayout';
import { FeedbackForm } from './FeedbackForm';
import { CorrectionSlideDown } from './CorrectionSlideDown';
import medalIcon from './assets/medal.svg';
import olympicFlameIcon from './assets/olympic-flame.svg';

interface ScheduleEventItemProps {
  event: OlympicsListableEvent;
  year: number;
  showAIDisclaimer: boolean;
  showFeedbackForm: boolean;
  onFeedbackResponse: () => void;
}

/**
 * Formats time from date string "YYYY-MM-DD HH:mm:ss" to HH.MM format
 */
function formatTime(dateString: string): string {
  const [, timePart] = dateString.split(' ');
  if (!timePart) return '00.00';
  const [hours, minutes] = timePart.split(':');
  return `${hours}.${minutes}`;
}

function listInFinnish(items: string[]): string {
  const clean = items.map((s) => s?.trim()).filter((s): s is string => Boolean(s));

  const n = clean.length;
  if (n === 0) return '';
  if (n === 1) return clean[0];
  if (n === 2) return `${clean[0]} ja ${clean[1]}`;

  return `${clean.slice(0, -1).join(', ')} ja ${clean[n - 1]}`;
}

export const ScheduleEventItem: React.FC<ScheduleEventItemProps> = ({
  event: listableEvent,
  year,
  showAIDisclaimer,
  showFeedbackForm,
  onFeedbackResponse,
}) => {
  const isFinished = listableEvent.progression === 'finished';
  const isCancelled = listableEvent.progression === 'cancelled';
  const showFinnishParticipants =
    (listableEvent.sportsType === 'individual' || listableEvent.sportsType === 'not_known') &&
    !isCancelled &&
    !isFinished &&
    listableEvent.finnishParticipants.length > 0;

  const hasIsFiUrl = listableEvent.metadata.urls.some((url) => url.includes('is.fi'));
  const showInsight =
    config.featureFlags.enableInsights &&
    listableEvent.metadata.insight !== null &&
    !isCancelled &&
    (hasIsFiUrl || config.featureFlags.enableReutersInsight);

  const showCorrection = listableEvent.metadata.correction !== null;
  const displayAIDisclaimer =
    showAIDisclaimer &&
    config.featureFlags.enableInsights &&
    (hasIsFiUrl || config.featureFlags.enableReutersInsight);

  const showTv = listableEvent.metadata.tv && !isCancelled && !isFinished;
  const formattedTime = formatTime(listableEvent.metadata.date);

  // Check if adminShowSources URL parameter is present
  const params = useMemo(() => new URLSearchParams(window.location.search), []);
  const showUrls = params.has('adminShowSources') && listableEvent.metadata.urls.length > 0;

  const isTeamSportWithParticipants =
    listableEvent.sportsType === 'team' && listableEvent.participants !== null;

  const renderTimeRow = () => {
    // For team sports with participants, don't show time row - it's handled by TeamSportLayout
    if (
      isTeamSportWithParticipants &&
      (listableEvent.progression === 'inprogress' ||
        listableEvent.progression === 'notstarted' ||
        listableEvent.progression === 'interrupted')
    ) {
      return null;
    }

    switch (listableEvent.progression) {
      case 'cancelled':
        return <div className='event-time'>{formattedTime} – Peruttu</div>;
      case 'finished':
        return null; // Time row not shown for finished events
      case 'inprogress':
        return <div className='event-time'>Käynnissä</div>;
      case 'interrupted':
        return <div className='event-time'>Keskeytetty</div>;
      default:
        return <div className='event-time'>{formattedTime}</div>;
    }
  };

  // Check if this is the opening ceremony event (id: 9999999)
  const isOpeningCeremony = listableEvent.id === 9999999;
  const hasIcon = isOpeningCeremony || listableEvent.metadata.medal;

  return (
    <div className={`event-item ${listableEvent.progression}`}>
      <div className={`event-title ${hasIcon ? 'has-icon' : ''}`}>
        {isOpeningCeremony && <img src={olympicFlameIcon} alt='' className='medal-icon' />}
        {listableEvent.metadata.medal && <img src={medalIcon} alt='' className='medal-icon' />}
        {listableEvent.name}
      </div>
      {renderTimeRow()}
      {/* Team sport layout for ongoing and upcoming events with participants */}
      {isTeamSportWithParticipants &&
        (listableEvent.progression === 'inprogress' ||
          listableEvent.progression === 'notstarted' ||
          listableEvent.progression === 'interrupted') && (
          <TeamSportLayout
            participantOne={{
              teamName: listableEvent.participants!.teamOne.teamName,
              countryId: listableEvent.participants!.teamOne.countryId,
            }}
            participantTwo={{
              teamName: listableEvent.participants!.teamTwo.teamName,
              countryId: listableEvent.participants!.teamTwo.countryId,
            }}
          >
            <span className='team-center-content'>
              {listableEvent.progression === 'inprogress'
                ? 'Käynnissä'
                : listableEvent.progression === 'interrupted'
                  ? 'Keskeytetty'
                  : formattedTime}
            </span>
          </TeamSportLayout>
        )}
      {isFinished && (
        <>
          {listableEvent.sportsType === 'individual' && listableEvent.results && (
            <ResultsIndividualEvent
              results={listableEvent.results}
              eventId={listableEvent.id}
              year={year}
            />
          )}
          {listableEvent.sportsType === 'team' && listableEvent.participants && (
            <ResultsTeamEvent participants={listableEvent.participants} />
          )}
        </>
      )}
      {showTv && <div className='event-tv'>{listableEvent.metadata.tv}</div>}
      {showFinnishParticipants && (
        <div className='event-finns'>
          Osallistujat: {listInFinnish(listableEvent.finnishParticipants)}
        </div>
      )}
      {(showInsight || displayAIDisclaimer || showCorrection) && (
        <div className='event-metadata-container'>
          {showInsight && (
            <>
              <div className='event-details'>{listableEvent.metadata.insight}</div>
              {showFeedbackForm && (
                <FeedbackForm
                  insight={listableEvent.metadata.insight!}
                  eventId={listableEvent.id}
                  progressStatus={listableEvent.progression}
                  urls={listableEvent.metadata.urls}
                  onResponse={onFeedbackResponse}
                />
              )}
            </>
          )}
          {displayAIDisclaimer && (
            <div className='event-ai-disclaimer'>
              Tiivistelmät on tehty tekoälyllä uutisten pohjalta.{' '}
              <a href={config.aiInfoArticleUrl} target='_blank' rel='noopener noreferrer'>
                Lue lisää
              </a>
            </div>
          )}
          {showCorrection && (
            <CorrectionSlideDown correction={listableEvent.metadata.correction!} />
          )}
        </div>
      )}
      {showUrls && (
        <div className='event-sources'>
          {listableEvent.metadata.urls.map((url, index) => (
            <div key={index}>
              <a href={url} target='_blank' rel='noopener noreferrer'>
                {url}
              </a>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
